import { default as axiosBase } from 'axios';
import { tokenStorage } from './tokenStorage';

export const API_BASE = process.env.REACT_APP_API_HOST.toString().replace(/"/g, ``);

const baseConfig = {
  baseURL: API_BASE + '/api',
  timeout: 30000,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'Content-Type': 'application/json',
  },
};

export const axios = axiosBase.create(baseConfig);

// Separate instance without interceptors, so a failing refresh can never
// trigger another refresh.
export const refreshClient = axiosBase.create(baseConfig);

export const REFRESH_URL = '/users/sign-in/refresh/';

// Never refresh-and-retry these: a 401 from the refresh endpoint means the
// session is over (retrying would wait on itself), and the auth endpoints
// don't use the access token.
const NO_REFRESH_URLS = ['/users/sign-in/', REFRESH_URL, '/users/logout/'];

const isNoRefreshUrl = (url = '') => NO_REFRESH_URLS.some(path => url.endsWith(path));

const redirectToSignIn = () => {
  if (window.location.pathname !== '/sign-in') {
    window.location.assign('/sign-in');
  }
};

let onAuthFailure = redirectToSignIn;

// Called once when the session can't be renewed (refresh token expired or
// revoked). Tokens are already cleared by then.
export const setAuthFailureHandler = handler => {
  onAuthFailure = handler || redirectToSignIn;
};

// All concurrent 401s share one in-flight refresh request.
let refreshPromise = null;

export const refreshAccessToken = () => {
  if (refreshPromise) return refreshPromise;

  const refresh = tokenStorage.getRefresh();
  if (!refresh) return Promise.reject(new Error('No refresh token'));

  refreshPromise = refreshClient
    .post(REFRESH_URL, { refresh })
    .then(({ data }) => {
      tokenStorage.set(data.access, data.refresh);
      return data.access;
    })
    .catch(error => {
      // Only a rejected token ends the session; network errors/5xx may be
      // transient, so keep the tokens and let the caller see the failure.
      const status = error?.response?.status;
      if (status === 400 || status === 401) {
        tokenStorage.clear();
        onAuthFailure();
      }
      throw error;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

axios.interceptors.request.use(config => {
  const access = tokenStorage.getAccess();
  if (access && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${access}`;
  }
  return config;
});

axios.interceptors.response.use(
  response => response,
  async error => {
    const original = error.config;
    const shouldRefresh =
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      !isNoRefreshUrl(original.url) &&
      tokenStorage.getRefresh();

    if (!shouldRefresh) return Promise.reject(error);

    original._retry = true;
    let access;
    try {
      access = await refreshAccessToken();
    } catch (_) {
      return Promise.reject(error);
    }
    original.headers = { ...original.headers, Authorization: `Bearer ${access}` };
    return axios(original);
  }
);
